import { QueryClient, useQuery } from 'react-query'
import { CloudFunctionErrorResponse, GetObfuscatedEnrollmentResponse } from '../../../pure-js/types/QueryParamTypes'
import { State } from '../hooks/useAppState'
import { getObfuscatedEnrollment } from './CloudFunctionsApiHandler'

export enum QueryKeys {
  OBFUSCATED_ENROLLMENT = 'OBFUSCATED_ENROLLMENT'
}

export type GetObfuscatedEnrollmentQueryResult = GetObfuscatedEnrollmentResponse | CloudFunctionErrorResponse | void

export const isGetObfuscatedEnrollmentResponse = (
  value: GetObfuscatedEnrollmentQueryResult | undefined
): value is GetObfuscatedEnrollmentResponse => !!value && 'enrollment' in value

/** Throws when the cloud function returns empty/error so React Query can retry instead of caching "not found". */
export const fetchObfuscatedEnrollmentForQuery = async (
  enrollmentId: string,
  enrollmentUserId?: string
): Promise<GetObfuscatedEnrollmentResponse> => {
  const result = await getObfuscatedEnrollment({ enrollmentId, enrollmentUserId })
  if (!isGetObfuscatedEnrollmentResponse(result)) {
    throw new Error(`Obfuscated enrollment not found: ${enrollmentId}`)
  }
  return result
}

export const resolveEnrollmentIdForQuery = (state: State): string | undefined =>
  state.enrollmentId || state.obfuscatedEnrollment?.id || undefined

export const obfuscatedEnrollmentQueryKey = (enrollmentId: string | null, enrollmentUserId?: string) =>
  [QueryKeys.OBFUSCATED_ENROLLMENT, enrollmentId, enrollmentUserId ?? null] as const

export const prefetchObfuscatedEnrollment = (
  queryClient: QueryClient,
  enrollmentId: string,
  enrollmentUserId?: string
) =>
  queryClient.prefetchQuery(obfuscatedEnrollmentQueryKey(enrollmentId, enrollmentUserId), () =>
    fetchObfuscatedEnrollmentForQuery(enrollmentId, enrollmentUserId)
  )

/** Best-effort prefetch — failures are logged and swallowed; RegisterPage will fetch on its own. */
export const prefetchObfuscatedEnrollmentSafely = async (
  queryClient: QueryClient,
  enrollmentId: string,
  enrollmentUserId?: string
): Promise<void> => {
  try {
    await prefetchObfuscatedEnrollment(queryClient, enrollmentId, enrollmentUserId)
  } catch (error) {
    console.warn('[prefetchObfuscatedEnrollment] failed, RegisterPage will fetch on navigate', enrollmentId, error)
  }
}

export const useEnrollment = (state: State, enrollmentUserId?: string) => {
  const enrollmentId = resolveEnrollmentIdForQuery(state) ?? null

  return useQuery<GetObfuscatedEnrollmentResponse>(
    obfuscatedEnrollmentQueryKey(enrollmentId, enrollmentUserId),
    () => fetchObfuscatedEnrollmentForQuery(enrollmentId!, enrollmentUserId),
    {
      enabled: !!enrollmentId,
      staleTime: 0,
      retry: 3,
      retryDelay: 1000
    }
  )
}

export const useObfuscatedEnrollment = (enrollmentId: string, enrollmentUserId?: string) =>
  useQuery<GetObfuscatedEnrollmentResponse>(
    obfuscatedEnrollmentQueryKey(enrollmentId || null, enrollmentUserId),
    () => fetchObfuscatedEnrollmentForQuery(enrollmentId, enrollmentUserId),
    {
      enabled: !!enrollmentId,
      staleTime: 0,
      retry: 3,
      retryDelay: 1000
    }
  )
